import { SentryTraced } from "@sentry/nestjs"
import { RequestExecutorError, SupernovaApiClient } from "@supernova-studio/client"
import { z } from "zod"

import { VaultService } from "../services/vault.service.js"
import { apiHostForEnvironment, commonFlags, getTargetEnv, SentryCommand, TargetEnv } from "../types/index.js"

export default class WhoAmICommand extends SentryCommand<object> {
  static override args = {}
  static override description = "Print information about currently logged in user"
  static override examples = ["<%= config.bin %> <%= command.id %>"]
  static override flags = commonFlags

  get commandId(): string {
    return WhoAmICommand.id
  }

  get configSchema() {
    return z.object({})
  }

  @SentryTraced()
  public async run(): Promise<void> {
    const env = getTargetEnv()

    const accessToken = await this.getAccessTokenOrExit(env)
    const user = await this.getUserOrExit(env, accessToken)

    this.log(`Logged in as ${user.email}`)
  }

  private async getAccessTokenOrExit(env: TargetEnv): Promise<string> {
    const vaultService = new VaultService()
    const tokens = await vaultService.tryGetTokensFromVault(env)
    if (!tokens) {
      this.error(
        "No active user session found. To log in try one of the following:\n" +
          "  - run `supernova login` to authenticate in browser\n" +
          "  - provide SUPERNOVA_TOKEN variable with a personal access token (can be generated in user profile settings)",
      )
    }

    return tokens.accessToken
  }

  private async getUserOrExit(env: TargetEnv, accessToken: string) {
    const apiClient = new SupernovaApiClient({ accessToken, host: apiHostForEnvironment(env) })

    try {
      const { user } = await apiClient.users.getMe()
      return user
    } catch (error) {
      // TODO Artem: Add status
      if (error instanceof RequestExecutorError && error.type === "ServerError") {
        this.error(
          "Current session has expired or has been revoked. To start a new session try one of the following:\n" +
            "  - run `supernova login` to authenticate in browser\n" +
            "  - provide SUPERNOVA_TOKEN variable with a personal access token (can be generated in user profile settings)",
        )
      }

      throw error
    }
  }
}
